<template>
  <DialogRoot v-model:open="dialogOpen" @update:open="handleCloseFinancingModal" :key="iterationOfComponent">
    <DialogPortal>
      <Transition enter-active-class="backdrop-enter" leave-active-class="backdrop-leave">
        <DialogOverlay v-if="dialogOpen" class="modal-backdrop" />
      </Transition>

      <Transition enter-active-class="modal-enter" leave-active-class="modal-leave">
        <DialogContent v-if="dialogOpen" class="modal-content financing-modal">
          <DialogTitle as="div" class="mb-6 lg:mb-10 flex justify-between items-start">
            <h4 class="">{{ modalTitle }}</h4>

            <DialogClose asChild class="cursor-pointer size-6 min-w-6 mt-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="text-accent">
                <path
                  d="M16.192 6.34424L11.949 10.5862L7.70697 6.34424L6.29297 7.75824L10.535 12.0002L6.29297 16.2422L7.70697 17.6562L11.949 13.4142L16.192 17.6562L17.606 16.2422L13.364 12.0002L17.606 7.75824L16.192 6.34424Z"
                  fill="currentColor"
                />
              </svg>
            </DialogClose>
          </DialogTitle>

          <DialogDescription class="sr-only">
            {{ t('components.financingModal.description', { carTitle: currentCarDetail?.model }) }}
          </DialogDescription>
          <div class="relative w-0 h-0 overflow-hidden opacity-0 pointer-events-none" ref="modalContentRef"></div>
          <ClientOnly>
            <template v-if="currentCarDetail">
              <!-- Kept while inquiring, so going back shows the same terms -->
              <SharedFinancingCalculator v-show="view === 'calculator'" :vehicle="currentCarDetail" @inquire="openInquiry" @print="comingSoon" @save="comingSoon" />
              <SharedFinancingInquiry
                v-if="inquiry"
                v-show="view === 'inquiry'"
                :vehicle="currentCarDetail"
                :input="inquiry.input"
                :quote="inquiry.quote"
                @back="view = 'calculator'"
                @sent="onInquirySent"
                @close="handleCloseFinancingModal(false)"
              />
            </template>
          </ClientOnly>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import type { GraphQLCarDetail } from '~/types/graphql';
  import type { FinancingInput, FinancingInquiryContact, Quote } from '#shared/financing';
  import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui';
  import type { Toast } from '~/components/GlobalToast/index.vue';

  interface EventData {
    event:
      'financing_calculator_started' | 'financing_calculator_completed' | 'financing_calculator_personal_data_form_started' | 'financing_calculator_personal_data_form_submitted';
    page_location: string;
    page_title: string;
    vehicle_id?: string;
    make?: string;
    model?: string;
    manufactured_at?: string;
    price?: number;
    fuel_type?: string;
    user_data?: {
      sha256_email_address?: string;
      sha256_phone_number?: string;
    };
  }

  const { lockScroll, unlockScroll } = useScrollLock();
  const currentCarDetail = ref<GraphQLCarDetail | null>(null);
  const eventBus = useEventBus();
  const { push } = useDataLayer();
  const { t } = useI18n();

  const dialogOpen = ref(false);
  const modalContentRef = ref<HTMLElement | null>(null);

  // The calculator, then the inquiry flow with the terms and the example it showed.
  const view = ref<'calculator' | 'inquiry'>('calculator');
  const inquiry = ref<{ input: FinancingInput; quote: Quote } | null>(null);
  const inquirySent = ref(false);
  watch(view, () => modalContentRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }));

  const modalTitle = computed(() => {
    if (view.value === 'calculator') return t('components.financingModal.title', { carTitle: currentCarDetail.value?.model });
    return t(inquirySent.value ? 'components.financingInquiry.thanks.title' : 'components.financingInquiry.title');
  });

  const vehicleEventData = () => {
    const vehicle = currentCarDetail.value;
    return {
      page_location: window.location.href,
      page_title: window.document.title,
      vehicle_id: vehicle?.internal_vehicle_no?.toString() || '',
      make: vehicle?.make?.name,
      model: vehicle?.model,
      manufactured_at: vehicle?.year_of_manufacture?.toString() || '',
      price: vehicle?.asking_price,
      fuel_type: vehicle?.fuel_type,
    };
  };

  const handleOpenFinancingModal = (carDetail: { carDetail: GraphQLCarDetail }) => {
    dialogOpen.value = true;
    currentCarDetail.value = carDetail.carDetail;
    view.value = 'calculator';
    inquiry.value = null;
    inquirySent.value = false;
    lockScroll();

    push({
      ...vehicleEventData(),
      event: 'financing_calculator_started',
      vehicle_id: carDetail.carDetail?.motiondata_id?.toString() || '',
    } satisfies EventData);
  };

  function openInquiry(payload: { input: FinancingInput; quote: Quote }) {
    if (!inquiry.value) push({ ...vehicleEventData(), event: 'financing_calculator_personal_data_form_started' } satisfies EventData);
    inquiry.value = payload;
    view.value = 'inquiry';
  }

  async function onInquirySent({ contact }: { contact: FinancingInquiryContact }) {
    inquirySent.value = true;
    push({
      ...vehicleEventData(),
      event: 'financing_calculator_personal_data_form_submitted',
      user_data: {
        sha256_email_address: await useShaHash(contact.email),
        sha256_phone_number: await useShaHash(contact.phone),
      },
    } satisfies EventData);
  }

  // Placeholders until the PDF (print/save) is built.
  const comingSoon = () => {
    eventBus.emit('add-global-toast', { show: true, message: t('components.financingModal.comingSoon'), type: 'foreground' } satisfies Toast);
  };

  const handleCloseFinancingModal = (v: boolean) => {
    if (!v) {
      dialogOpen.value = false;
      unlockScroll();
      currentCarDetail.value = null;
    }
  };

  eventBus.on('open-financing-modal', handleOpenFinancingModal);

  const iterationOfComponent = ref(0);
  const route = useRoute();
  watch(
    () => route.params.slug,
    (newSlug, oldSlug) => {
      if (newSlug !== oldSlug) {
        view.value = 'calculator';
        iterationOfComponent.value++;
      }
    }
  );
</script>

<style scoped>
  .financing-modal {
    overflow-y: auto;
    /* Two columns (terms, representative example) as in the calculator design. */
    max-width: 1120px;
  }
</style>
